import { ClickAwayListener } from "@nova/component-click-away-listener"
import { ChevronRight, IconComponent, SmallIcon } from "@nova/component-icons"
import { zIndex } from "@nova/constants"
import { useTranslation } from "@nova/use-translation"
import classNames from "classnames"
import { Fragment, MouseEvent, useEffect, useMemo, useRef, useState } from "react"

type Position = {
    top?: number
    left?: number
    right?: number
}

export type ContextMenuSubmenu = Pick<ContextMenuProps, "sections" | "sectionHeaders">

export type ContextMenuItem = {
    title: string
    Icon?: IconComponent
    iconClass?: string
    isDestructive?: boolean
    onClick?: (e: MouseEvent) => void

    submenu?: ContextMenuSubmenu
}

type MenuPanelProps = {
    sections: ContextMenuItem[][]
    sectionHeaders?: string[]
    onBlur: () => void
    keepOpenOnSelect?: boolean
    iconColor?: string
    containerClassName?: string
}

type ContextMenuProps = MenuPanelProps & {
    isFixedPosition?: boolean
    isAutoPosition?: boolean
    top?: number
    left?: number
    right?: number
} & React.HTMLAttributes<HTMLElement>

const MenuPanel = ({
    sections,
    sectionHeaders,
    onBlur,
    keepOpenOnSelect,
    iconColor,
    containerClassName,
}: MenuPanelProps) => {
    const { t } = useTranslation()

    const [itemBeingHovered, setItemBeingHovered] = useState<string>()
    const [openSubmenuId, setOpenSubmenuId] = useState<string | null>(null)
    const [submenuStyle, setSubmenuStyle] = useState<{ top: number; left: number } | null>(null)

    const containerRef = useRef<HTMLDivElement>(null)
    const submenuWrapperRef = useRef<HTMLDivElement>(null)
    const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map())

    const activeSubmenu = useMemo<ContextMenuSubmenu | null>(() => {
        if (!openSubmenuId) return null
        for (let i = 0; i < sections.length; i++) {
            for (let j = 0; j < sections[i].length; j++) {
                if (`${i}_${j}` === openSubmenuId) return sections[i][j].submenu ?? null
            }
        }
        return null
    }, [openSubmenuId, sections])

    const submenuTop = openSubmenuId ? (rowRefs.current.get(openSubmenuId)?.offsetTop ?? 0) : 0

    useEffect(() => {
        if (!activeSubmenu) {
            setSubmenuStyle(null)
            return
        }
        const container = containerRef.current
        const wrapper = submenuWrapperRef.current
        if (!container || !wrapper) return

        const containerRect = container.getBoundingClientRect()
        const wrapperRect = wrapper.getBoundingClientRect()

        // Horizontal: default to right side, flip left if it would overflow
        const left =
            containerRect.right + wrapperRect.width > window.innerWidth
                ? -wrapperRect.width
                : containerRect.width + 4

        // Vertical: clamp upward if it would overflow
        const screenTop = containerRect.top + submenuTop
        const overflow = screenTop + wrapperRect.height - window.innerHeight
        const top = overflow > 0 ? submenuTop - overflow : submenuTop

        setSubmenuStyle({ top, left })
    }, [activeSubmenu, submenuTop])

    return (
        <div className="relative">
            <div
                ref={containerRef}
                className={classNames(
                    "relative bg-gray-700 rounded-lg py-1 overflow-hidden shadow-context-menu select-none",
                    containerClassName
                )}
            >
                {sections.map((section, i) => (
                    <Fragment key={i}>
                        {sectionHeaders && sectionHeaders.length > i && (
                            <div className="pl-3 pr-4 py-1">
                                <small className="xs pl-8 text-gray-300">{sectionHeaders[i]}</small>
                            </div>
                        )}
                        {section.map((item, j) => {
                            const id = `${i}_${j}`
                            const sectionHasIcons = section.some((s) => !!s.Icon)
                            const isHovered = itemBeingHovered === id
                            const hasSubmenu = !!item.submenu

                            return (
                                <div
                                    key={id}
                                    ref={(el) => {
                                        if (el) rowRefs.current.set(id, el)
                                        else rowRefs.current.delete(id)
                                    }}
                                    className={classNames(
                                        "h-7 flex items-center gap-3 py-1 pl-3 pr-4 cursor-pointer",
                                        {
                                            "bg-gray-500": isHovered || openSubmenuId === id,
                                        }
                                    )}
                                    onMouseEnter={() => {
                                        setItemBeingHovered(id)
                                        if (hasSubmenu) setOpenSubmenuId(id)
                                        else setOpenSubmenuId(null)
                                    }}
                                    onMouseLeave={() => setItemBeingHovered(undefined)}
                                    onClick={(e) => {
                                        if (hasSubmenu) {
                                            setOpenSubmenuId(id)
                                            e.stopPropagation()
                                            return
                                        }
                                        if (!keepOpenOnSelect) onBlur()
                                        item.onClick?.(e)
                                        e.stopPropagation()
                                    }}
                                >
                                    {item.Icon ? (
                                        <SmallIcon
                                            Icon={item.Icon}
                                            className={classNames({
                                                "text-gray-100":
                                                    !isHovered && !item.isDestructive && !iconColor,
                                                "text-blue-500":
                                                    isHovered && !item.isDestructive && !iconColor,
                                                "text-red-500": item.isDestructive,
                                            })}
                                            style={iconColor ? { color: iconColor } : undefined}
                                        />
                                    ) : sectionHasIcons ? (
                                        <div className="w-5" />
                                    ) : null}
                                    <small
                                        className={classNames("flex-1 whitespace-nowrap", {
                                            "text-gray-0": !item.isDestructive,
                                            "text-red-500": item.isDestructive,
                                        })}
                                    >
                                        {t(item.title)}
                                    </small>
                                    {hasSubmenu && (
                                        <SmallIcon Icon={ChevronRight} className="text-gray-100" />
                                    )}
                                </div>
                            )
                        })}

                        {i < sections.length - 1 && (
                            <div key={`separator_${i}`} className="my-1 h-px w-full bg-white-200" />
                        )}
                    </Fragment>
                ))}
            </div>
            {activeSubmenu && (
                <div
                    ref={submenuWrapperRef}
                    className={classNames("absolute", { invisible: !submenuStyle })}
                    style={
                        submenuStyle
                            ? { top: submenuStyle.top, left: submenuStyle.left }
                            : { top: submenuTop, left: "100%" }
                    }
                >
                    <MenuPanel
                        sections={activeSubmenu.sections}
                        sectionHeaders={activeSubmenu.sectionHeaders}
                        onBlur={onBlur}
                        keepOpenOnSelect={keepOpenOnSelect}
                        iconColor={iconColor}
                    />
                </div>
            )}
        </div>
    )
}

export const ContextMenu = ({
    sections,
    sectionHeaders,
    onBlur,
    isFixedPosition = false,
    isAutoPosition,
    top,
    left,
    right,
    keepOpenOnSelect,
    iconColor,
    containerClassName,
    ...rest
}: ContextMenuProps) => {
    const [fixedPosition, setFixedPosition] = useState<{
        top?: number
        left?: number
        right?: number
    }>()
    const [invertYPosition, setInvertYPosition] = useState<boolean>()
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") onBlur()
        }
        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [onBlur])

    useEffect(() => {
        const container = containerRef.current
        if (!container) return

        if (!isFixedPosition) {
            const rect = container.getBoundingClientRect()
            const { y: top, height } = rect
            const y2 = height + top
            setInvertYPosition(y2 - window.innerHeight > 0)
            return
        }

        if (
            top === undefined ||
            (left === undefined && right === undefined) ||
            (left !== undefined && right !== undefined)
        )
            return

        const rect = container.getBoundingClientRect()
        const { width, height } = rect
        const calculated: Position = {}

        const y2 = height + top
        const dy = y2 - window.innerHeight
        calculated.top = dy > 0 ? top - dy : top

        if (left !== undefined) {
            const x2 = width + left
            const dx = x2 - window.innerWidth
            calculated.left = dx > 0 ? left - dx : left
        } else if (right !== undefined) {
            const x2 = width + right
            const dx = x2 - window.innerWidth
            calculated.right = dx > 0 ? right - dx : right
        }

        setFixedPosition(calculated)
    }, [top, left, right, isFixedPosition])

    return (
        <ClickAwayListener
            className={classNames(rest.className, {
                invisible:
                    (isFixedPosition && !fixedPosition) ||
                    (!isFixedPosition && invertYPosition === undefined),
                fixed: isFixedPosition,
                absolute: !isFixedPosition,
            })}
            style={{
                ...rest.style,
                top: fixedPosition
                    ? fixedPosition?.top
                    : !invertYPosition
                      ? rest.style?.top
                      : undefined,
                bottom: invertYPosition ? rest.style?.top : undefined,
                left: fixedPosition ? fixedPosition?.left : rest.style?.left,
                right: fixedPosition ? fixedPosition?.right : rest.style?.right,
                ...(isAutoPosition ? { top: "auto", left: "auto", right: "auto" } : {}),
                zIndex: rest.style?.zIndex ?? zIndex.SidePanel + 100,
            }}
            onClickAway={onBlur}
        >
            <div ref={containerRef}>
                <MenuPanel
                    sections={sections}
                    sectionHeaders={sectionHeaders}
                    onBlur={onBlur}
                    keepOpenOnSelect={keepOpenOnSelect}
                    iconColor={iconColor}
                    containerClassName={containerClassName}
                />
            </div>
        </ClickAwayListener>
    )
}
